'use client';
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * @primitive Form
 * ROLE: form
 * ARIA: aria-busy during submission.
 * KEYBOARD: standard form submission (Enter inside non-textarea field).
 * FOCUS: tracks submitter on submit; restores per result kind on revalidation.
 *        On error: focus moves to first Field with a non-empty error.
 *        On success: focus moves to <Form.SuccessTarget> if rendered, else next
 *        focusable element after the form, else the page's main heading.
 * LIVE-REGION: Field.Error live regions inside the form announce errors.
 * PRM: pending-state spinner respects PRM (no animation when prefers-reduced-motion).
 * FORCED-COLORS: submit button uses standard rules.
 * RSC: CLIENT.
 * WAI-APG: Form Validation + focus-management.
 * SUBSTRATE: custom (no Radix Form — Radix doesn't address Next.js server-action
 *            focus restoration).
 */
import { useCallback, useLayoutEffect, useRef, useState, } from 'react';
import { FormSuccessTarget } from './FormSuccessTarget';
function findFirstErrorField(form) {
    // Look for the first control with aria-invalid="true" in DOM order.
    return form.querySelector('[aria-invalid="true"]');
}
function findSuccessTarget(form) {
    return form.querySelector('[data-form-success-target="true"]');
}
function findNextFocusableAfterForm(form) {
    // Walk forward in the document to find a focusable element after the form.
    const all = Array.from(document.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'));
    for (const el of all) {
        // eslint-disable-next-line no-bitwise
        if (form.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING) {
            return el;
        }
    }
    return null;
}
function findMainHeading() {
    return (document.querySelector('main h1') ??
        document.querySelector('h1'));
}
function FormInner({ action, onResult, pendingState, children, className, }) {
    const [submitState, setSubmitState] = useState('idle');
    // Track the last result to drive post-render focus restoration.
    const pendingFocusResult = useRef(null);
    const formRef = useRef(null);
    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        if (submitState === 'pending')
            return;
        setSubmitState('pending');
        const formData = new FormData(e.currentTarget);
        try {
            const result = await action(formData);
            pendingFocusResult.current = result;
            onResult?.(result);
        }
        catch {
            // Treat thrown errors as a generic error result — surface to UI
            // without swallowing. Production callers should catch in their action.
            const result = { kind: 'error' };
            pendingFocusResult.current = result;
            onResult?.(result);
        }
        finally {
            setSubmitState('idle');
        }
    }, [action, onResult, submitState]);
    // After re-render (which may include updated error props on Fields), move focus.
    useLayoutEffect(() => {
        const result = pendingFocusResult.current;
        if (result === null || !formRef.current)
            return;
        pendingFocusResult.current = null;
        const form = formRef.current;
        if (result.kind === 'error') {
            const target = findFirstErrorField(form);
            target?.focus();
        }
        else {
            // Success path
            const target = findSuccessTarget(form) ??
                findNextFocusableAfterForm(form) ??
                findMainHeading();
            target?.focus();
        }
    });
    const isPending = submitState === 'pending';
    return (_jsx("form", { ref: formRef, onSubmit: handleSubmit, "aria-busy": isPending ? true : undefined, className: className, noValidate: true, children: isPending && pendingState != null ? pendingState : children }));
}
/**
 * Form wrapper for server-action and client-async submission with focus
 * restoration.
 *
 * ```tsx
 * <Form action={save} onResult={handleResult}>
 *   <Field label="Email"><Input type="email" name="email" /></Field>
 *   <Button type="submit">Save</Button>
 *   <Form.SuccessTarget />
 * </Form>
 * ```
 */
export const Form = Object.assign(FormInner, {
    SuccessTarget: Object.assign(FormSuccessTarget, { displayName: 'Form.SuccessTarget' }),
});
//# sourceMappingURL=Form.js.map